/**
 * Admin › Finanças › Ofertas de incentivo, the merchants' Smart Campaigns (W10-DELIVERY-FOOD-LEVERS, D494, ADR-0111; lever L7) - the pure view-model behind the platform's view of
 * the campaigns a market's merchants run: which can still be ended (the kill switch), how much of its co-investment each has drawn, and what the platform and the merchant have
 * each paid. The merchant declares and the platform co-invests under the governed `incentives.merchantCofundPolicy`; nothing is created here (a platform operator only watches and
 * ends). Nothing here names a country, a currency or an amount.
 */
import type { OfferTone } from './active-incentives.js';
import type { AdminSmartCampaignsView, SmartCampaignEndReason, SmartCampaignStatus, SmartCampaignView } from '@vello/api-client';

export type SmartCampaignTone = OfferTone;

/** A campaign an operator may still end: it has not ended and was not refused. */
export function campaignCanBeEnded(c: Pick<SmartCampaignView, 'status'>): boolean {
  return c.status === 'ACTIVE' || c.status === 'PAUSED' || c.status === 'PROPOSED';
}

export function campaignStatusTone(status: SmartCampaignStatus): SmartCampaignTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PAUSED':
    case 'PROPOSED':
      return 'warning';
    case 'REFUSED':
      return 'danger';
    default:
      return 'neutral';
  }
}

const minor = (m: { readonly amountMinor: string } | undefined): bigint => (m !== undefined && /^\d+$/.test(m.amountMinor) ? BigInt(m.amountMinor) : 0n);

/** The share of the reserved co-investment already drawn, in basis points (0 when nothing is reserved), never above 10 000. */
export function campaignDrawnBp(c: Pick<SmartCampaignView, 'cap' | 'usage'>): number {
  const cap = minor(c.cap);
  if (cap <= 0n) return 0;
  const bp = (minor(c.usage.platformDrawn) * 10_000n) / cap;
  return Number(bp > 10_000n ? 10_000n : bp);
}

/** The reasons an operator may end a campaign with, in the server's order: the system's own (window over) and the merchant's own are never offered. */
export function operatorEndReasons(view: Pick<AdminSmartCampaignsView, 'endReasons'>): readonly SmartCampaignEndReason[] {
  return view.endReasons.filter((r) => r !== 'WINDOW_OVER' && r !== 'MERCHANT_ENDED');
}

export const DEFAULT_CAMPAIGN_END_REASON: SmartCampaignEndReason = 'OPERATOR_KILL_SWITCH';

/** Newest first: the live ones before the past ones, then by creation instant. */
export function sortCampaigns(rows: readonly SmartCampaignView[]): readonly SmartCampaignView[] {
  const rank = (c: SmartCampaignView): number => (campaignCanBeEnded(c) ? 0 : 1);
  return [...rows].sort((a, b) => rank(a) - rank(b) || b.createdAt.localeCompare(a.createdAt) || a.campaignId.localeCompare(b.campaignId));
}
